<template>
    <div class="q-comprehensive-filter">
		<v-menu
			v-if="activatorId"
			v-model="doShowMenu"
			:activator="`#${activatorId}`"
			offset-y
			:nudge-top="-5"
			:min-width="280"
			:close-on-content-click="false"
			:z-index="8"
			content-class="q-comprehensive-filter__content"
		>
			<template v-if="doShowMenu || doShowCurrentFilterCategory">
				<v-card v-if="!doShowCurrentFilterCategory" class="q-comprehensive-filter__card pa-2">
					<v-list class="q-comprehensive-filter__filters-list">
						<v-list-item
							v-for="(item, itemKey) in options" :key="itemKey"
							@click.stop="onFilterCategorySelect(itemKey)"
						>
							<v-list-item-icon>
								<v-icon v-if="item.icon" color="#ADADAD">{{ item.icon }}</v-icon>
							</v-list-item-icon>
							<v-list-item-title>
								{{ item.label }}
								<v-tooltip v-if="item.tooltip" top>
									<template v-slot:activator="{ on, attrs }">
										<v-icon :color="color" v-bind="attrs" v-on="on">icon-info-circle-q</v-icon>
									</template>
									<div v-html="item.tooltip" style="max-width: 25rem" />
								</v-tooltip>
							</v-list-item-title>
							<div v-if="!item.isDefaultValue" class="q-comprehensive-filter__dot ml-4" />
						</v-list-item>
					</v-list>
				</v-card>

				<v-card v-else-if="doShowCurrentFilterCategory && currentFilterCategoryItem" class="q-comprehensive-filter__card pa-4">
					<ComprehensiveAgencyFilter
						v-if="currentFilterCategoryId === 'Agency'"
						v-model="currentFilterCategoryValue"
						:item="currentFilterCategoryItem"
						:action-btn-class="actionBtnClass"
						:color="color"
						@cancel="closeCurrentFilterCategoryMenu"
						@save="updateCurrentFilterCategoryValue"
					/>
					<ComprehensiveDateFilter
						v-else-if="options[currentFilterCategoryKey].type === 'datePeriod'"
						v-model="currentFilterCategoryValue"
						:item="currentFilterCategoryItem"
						:action-btn-class="actionBtnClass"
						:color="color"
						@cancel="closeCurrentFilterCategoryMenu"
						@save="updateCurrentFilterCategoryValue"
					/>
					<template v-else>
						<div class="q-comprehensive-filter__filters-label">{{ currentFilterCategoryItem.label }}</div>
						<component
							v-if="currentFilterCategoryItem.component"
							:is="currentFilterCategoryItem.component"
							v-model="currentFilterCategoryValue"
							:options="currentFilterCategoryItem.options"
							:color="color"
						/>
						<div class="q-comprehensive-filter__filters-actions">
							<v-btn :class="actionBtnClass" outlined :color="color" @click="closeCurrentFilterCategoryMenu">
								Cancel
							</v-btn>
							<v-btn :class="actionBtnClass" :disabled="!currentFilterCategoryValueChanged" :color="color" @click="updateCurrentFilterCategoryValue">
								{{ currentFilterCategoryItem.actionLabel || 'Add' }}
							</v-btn>
						</div>
					</template>
				</v-card>
			</template>
		</v-menu>

		<div v-if="filterTags.length" class="q-comprehensive-filter__filters-container">
			<template v-for="filter in filterTags">
				<v-chip
					:key="filter.key"
					close outlined label
					color="#707070"
					close-icon="icon-x-close-q"
					class="q-comprehensive-filter__filters-chip"
					@click="onFilterCategorySelect(filter.key)"
					@click:close="resetFilter(filter.key)"
				>
					<strong>{{ options[filter.key].label }}</strong>: {{ getFilterLabeledValue(filter.key, filter.value) }}
				</v-chip>
			</template>
			<v-btn class="q-comprehensive-filter__reset-btn" text :color="color" @click="resetFilters">
				Clear All
			</v-btn>
		</div>
    </div>
</template>

<script>
import ComprehensiveDateFilter from '@/components/utils/ComprehensiveFilters/ComprehensiveDateFilter.vue'
import QComprehensiveFiltersCheckboxes from '@/components/utils/ComprehensiveFilters/QComprehensiveFiltersCheckboxes.vue'
import ComprehensiveAgencyFilter from '@/components/utils/ComprehensiveFilters/ComprehensiveAgencyFilter.vue'
import { statTypes } from './statTypes'

const statTypeOptions = [
	{ label: 'Total Agency', value: statTypes.totalAgency },
	{ label: 'Baseshop', value: statTypes.baseshop },
	{ label: 'Personal', value: statTypes.personal },
	{ label: 'Master Agency', value: statTypes.masterAgency },
]

export default {
    components: {
		ComprehensiveAgencyFilter,
		ComprehensiveDateFilter,
		QComprehensiveFiltersCheckboxes,
	},
    props: {
        value: { type: Object, required: true },
        filtersData: { type: Object, required: true  },
        filterKeys: { type: Array, default: () => [] },
        defaultValue: { type: Object },
		activatorId: { type: String, default: '' },
        actionBtnClass: { type: String, default: 'action-btn' },
        color: { type: String, default: '#46c3b2' },
    },
    data () {
        return {
			doShowMenu: false,
			doShowCurrentFilterCategory: false,
			currentFilterCategoryKey: '',
			currentFilterCategoryValue: null,
            defaultFilters: {},
            filterTagsOrder: [],
        }
    },
    created () {
		this.getDefaultFilters()
		this.getFilterTagsOrder()
    },
    computed: {
		filters: {
			get () { return this.value },
			set (val) { this.$emit('input', val) },
		},
        options () {
            return this.filterKeys
				.reduce((acc, key) => {
					const item = this.getOptions(key)
					if (item) {
						acc[key] = item
					}
					return acc
				}, {})
        },
		currentFilterCategoryId () {
			if (!this.currentFilterCategoryKey) { return null }
			return this.options[this.currentFilterCategoryKey]?.id
		},
		currentFilterCategoryItem () {
			if (!this.currentFilterCategoryKey) { return null }
			let component = null
			if (this.currentFilterCategoryId === 'Agency') {
				component = ComprehensiveAgencyFilter
			}
			if (this.options[this.currentFilterCategoryKey].type === 'datePeriod') {
				component = ComprehensiveDateFilter
			}
			if (this.options[this.currentFilterCategoryKey].type === 'checkboxes') {
				component = QComprehensiveFiltersCheckboxes 
			}
			return {
				...this.options[this.currentFilterCategoryKey],
				component,
			}
		},
		currentFilterCategoryValueChanged () {
			return this.checkFilterValueChanges(this.currentFilterCategoryValue, this.filters[this.currentFilterCategoryKey])
		},
		isDefaultFilters () {
			return Object.keys(this.filters)
				.every((key) => !this.checkFilterValueChanges(this.filters[key], this.defaultFilters[key]))
		},
		filterTags () {
			return this.filterTagsOrder.map((key) => ({ key, value: this.filters[key] }))
		},
    },
    watch: {
		doShowMenu (val) {
			if (!val) {
				this.currentFilterCategoryKey = ''
				this.doShowCurrentFilterCategory = false
			}
			this.$emit('menu-update', val)
		},
		currentFilterCategoryKey (val) {
			this.currentFilterCategoryValue = val ? this.filters[val] : null
		},
        filters: {
			deep: true,
			handler (val) {
			   this.$emit('input', val)
		   },
		},
		defaultValue: {
			deep: true,
			handler () {
				this.getDefaultFilters()
			},
		},
		isDefaultFilters: {
			immediate: true,
			handler (val) {
				this.$emit('default-filters', val)
			},
		},
    },
    methods: {
		getDefaultFilters () {
			const filters = this.defaultValue || this.value
			this.defaultFilters = this.copyFilters(filters)
		},
		getFilterTagsOrder () {
			this.filterTagsOrder = this.filterKeys
				.map((key) => this.checkFilterValueChanges(this.filters[key], this.defaultFilters[key]) && key)
				.filter(Boolean)
		},
        getOptions (key) {
			if (this.filtersData?.[key]) {
				return {
					...this.filtersData[key],
					isDefaultValue: !this.checkFilterValueChanges(this.filters[key], this.defaultFilters[key]),
				}
            }
            return undefined
        },
		setFilterDefaultValue (key) {
			this.filters[key] = this.defaultFilters[key]
        },
		onFilterCategorySelect (key) {
			this.doShowMenu = true
			this.currentFilterCategoryKey = key
			this.doShowCurrentFilterCategory = true
		},
		updateCurrentFilterCategoryValue () {
			if (['SubmittedDate', 'ReceivedDate'].includes(this.currentFilterCategoryKey)) {
				if (this.currentFilterCategoryKey === 'SubmittedDate') {
					this.filters.ReceivedDate = { startDate: null, endDate: null }
					this.updateFilterTagsOrder('ReceivedDate')
				} else {
					this.filters.SubmittedDate = { startDate: null, endDate: null }
					this.updateFilterTagsOrder('SubmittedDate')
				}
			}
			this.filters[this.currentFilterCategoryKey] = this.currentFilterCategoryValue
			this.updateFilterTagsOrder(this.currentFilterCategoryKey)
			this.currentFilterCategoryKey = ''
			this.doShowCurrentFilterCategory = false
			this.doShowMenu = false
		},
		closeCurrentFilterCategoryMenu () {
			this.currentFilterCategoryKey = ''
			this.doShowCurrentFilterCategory = false
		},
		updateFilterTagsOrder (key) {
			const isDefaultValue = !this.checkFilterValueChanges(this.filters[key], this.defaultFilters[key])
			if (this.filterTagsOrder.includes(key)) {
				if (isDefaultValue) {
					const index = this.filterTagsOrder.indexOf(key)
					this.filterTagsOrder = [
						...this.filterTagsOrder.slice(0, index),
						...this.filterTagsOrder.slice(index + 1),
					]
				}
			} else {
				if (!isDefaultValue) {
					this.filterTagsOrder = [...this.filterTagsOrder, key]
				}
			}
		},
		checkFilterValueChanges (changed, original) {
			// objects
			if (
				typeof changed === 'object' && changed !== null &&
				typeof original === 'object' && original !== null
			) {
				const changedKeys = Object.keys(changed)
				const originalKeys = Object.keys(original)
				const hasSameLength = changedKeys.length === originalKeys.length
				const hasSameValues = changedKeys.every((key) => changed[key] === original[key])
				if (hasSameLength && hasSameValues) { return false }
				return true
			}
			// strings
			return changed !== original
		},
		copyFilters (filters = {}) {
			return Object.entries(filters).reduce((acc, [key, value]) => {
				const copy = (typeof value === 'object' && value !== null) ? { ...value } : value
				acc[key] = copy
				return acc
			}, {})
		},
		getFilterLabeledValue (key, filter) {
			if (this.options[key].type === 'datePeriod') {
				const startDate = filter.startDate
					? this.formatDate(filter.startDate, 'MM/DD/YYYY')
					: null
				const endDate = filter.endDate
					? this.formatDate(filter.endDate, 'MM/DD/YYYY')
					: this.formatToday('MM/DD/YYYY')
				return (startDate && endDate)
					? `${startDate} - ${endDate}`
					: `by ${endDate}`
			}
			if (this.options[key].id === 'Agency') {
				const agent = filter.Agent ? `${filter.Agent.AgentName} - ${filter.Agent.LeadershipLevel} (${filter.Agent.AgentCode})` : ''
				const stat = statTypeOptions.find(({ value }) => value === filter.statType)?.label
				return agent ? `${stat} (${agent})` : stat
			}
			return filter
		},

		// expose
		showMenu () {
			this.doShowMenu = true
		},
		hideMenu () {
			this.doShowMenu = false
		},
		resetFilter (key) {
			this.setFilterDefaultValue(key)
			this.updateFilterTagsOrder(key)
		},
		resetFilters () {
			this.filterKeys.forEach((key) => this.setFilterDefaultValue(key))
			this.filterTagsOrder = []
        },
    },
}
</script>

<style lang="scss">
.q-comprehensive-filter__content {
	border-radius: 8px;
	box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.q-comprehensive-filter__card {
	border-radius: inherit !important;
	border: 1px solid #E4E4E4 !important;
	background-color: #FFF !important;
}

.q-comprehensive-filter__filters-list {
	padding: 0;

	.v-list-item {
		padding: 0 0.75rem 0 0.5rem;

		&::before,
		&::after {
			border-radius: 6px;
		}
	}

	.v-list-item__icon {
		margin: 14px 1rem 14px 0 !important;
	}
}

.q-comprehensive-filter__dot {
	$size: 8px;
	flex: 0 0 auto;
	width: $size;
	height: $size;
	border-radius: $size / 2;
	background-color: #079455;
}

.q-comprehensive-filter__filters-actions {
	margin-top: 1.5rem;
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 0.5rem 1rem;
}

.q-comprehensive-filter__filters-label {
	color: #707070;
	font-size: 14px;
	font-weight: 400;
	line-height: 1.5;
}
</style>

<style lang="scss" scoped>
.q-comprehensive-filter {
	.q-comprehensive-filter__filters-container {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem 1rem;
	}

	.q-comprehensive-filter__filters-chip {
		&.v-chip {
			background-color: #F8F8F8 !important;
			border-radius: 6px !important;
			border: 1px solid #E4E4E4 !important;
		}
	}

	.q-comprehensive-filter__reset-btn {
		height: 32px;
		text-transform: none;
		letter-spacing: normal;
		font-weight: 700;
		
		::v-deep .v-btn__content {
			line-height: 1.2;
			border-bottom: 2px solid currentColor;
		}
	}
}
</style>
